import { useContext, useEffect } from 'react';
import { useMatch } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BrandingContainer } from '@/containers/AppTheming/BrandingContainer';
import { ChatWidgetContainer } from '@/containers/Chat.Widget';
import { ProjectToolsContainer } from '@/containers/GroupProject/Project.Tools.Container';
import { SurveyResponseFilterContainer } from '@/containers/GroupProject/Survey.ResponseFilter.Container';
import { SurveyResponsesContainer } from '@/containers/GroupProject/Survey.ResponsesState.Container';
import { ProjectEntitiesContainer } from '@/containers/Project.Entities';
import { SEGMENTS_QUERY_KEY, fetchSegmentsForProject } from '@/screens/Segments/SegmentList';
import { ProjectDetailsContext } from './Context';
import { ProjectContainer } from './Project.Container';
import { HackathonChatContextBridge } from './HackathonChatContextBridge';
import { ProjectTabs } from './Project.Tabs';

// Projects that get the Segments subtab — must match the gate in
// Project.Tabs.Container. Prefetch kicks in for these so the Segments
// tab loads instantly on click instead of waiting on a cold fetch.
const SEGMENTS_PREFETCH_PROJECT_IDS = new Set<number>([11]);

// Projects that should have the global chat widget default to the
// hackathon agent context (same agent the segmentation builder uses).
const HACKATHON_CHAT_PROJECT_IDS = new Set<number>([11]);

type Props = unknown;

const ProjectTabStateContainer = (props: Props) => {
  const ctx = useContext(ProjectDetailsContext);
  const queryClient = useQueryClient();

  const entities = useMatch({
    path: `/projects/:projectId/entities`,
  });

  const projectId = ctx.query.data?.project?.id;
  const isReady = !!projectId;

  // Prefetch segments for projects that have the Segments subtab, so the
  // user sees instant results when they click the tab. Uses react-query's
  // prefetch so the cache is primed and subsequent mounts read synchronously.
  useEffect(() => {
    if (!projectId) return;
    const numericId = Number(projectId);
    if (!SEGMENTS_PREFETCH_PROJECT_IDS.has(numericId)) return;
    void queryClient.prefetchQuery({
      queryKey: SEGMENTS_QUERY_KEY(numericId),
      queryFn: () => fetchSegmentsForProject(numericId),
      staleTime: 5 * 60 * 1000,
    });
  }, [projectId, queryClient]);

  // Force the global chat widget to default to the hackathon agent for
  // project 11 (and anything else in HACKATHON_CHAT_PROJECT_IDS). The
  // bridge dispatches via DispatchPageContextDataContext, which overrides
  // whatever build-default-context.ts resolves. Individual screens
  // (SegmentProfile) can still dispatch their own segment-scoped context
  // on top of this — last write wins.
  const useHackathonChat =
    projectId != null && HACKATHON_CHAT_PROJECT_IDS.has(Number(projectId));

  return (
    <SurveyResponsesContainer>
      <SurveyResponseFilterContainer>
        <ProjectEntitiesContainer
          enabled={!!entities && isReady}
          projectId={projectId}>
          <ProjectToolsContainer>
            {useHackathonChat && <HackathonChatContextBridge />}
            <ProjectTabs />
          </ProjectToolsContainer>
        </ProjectEntitiesContainer>
      </SurveyResponseFilterContainer>
    </SurveyResponsesContainer>
  );
};

export const ProjectMain = (props: Props) => {

  const enabled = true;

  return (
    <>
      <BrandingContainer>
        <ProjectContainer>
          <ProjectTabStateContainer />
        </ProjectContainer>
        {enabled && <ChatWidgetContainer />}
      </BrandingContainer>
    </>
  );
};

ProjectMain.displayName = 'Project.Main';